import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

import { motion } from 'motion/react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon/icon'

const TRANSITION = {
  duration: 0.4,
  ease: [0.32, 0.72, 0, 1] as [number, number, number, number]
}

export interface OmniTableEntityCellProps {
  children: ReactNode
  className?: string
  /** Called with the new state each time the reader expands or collapses the name. */
  onExpandedChange?: (expanded: boolean) => void
}

/** Expands overflowing entity names within the row, preserving the supplied link. */
export function OmniTableEntityCell({
  children,
  className,
  onExpandedChange
}: OmniTableEntityCellProps) {
  const reducedMotion = useReducedMotion()
  const transition = reducedMotion ? { duration: 0 } : TRANSITION
  const contentRef = useRef<HTMLSpanElement>(null)
  const contentId = useId()
  const [expanded, setExpanded] = useState(false)
  const [showFullText, setShowFullText] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const [collapsedHeight, setCollapsedHeight] = useState(0)

  useEffect(() => {
    const content = contentRef.current
    if (!content || showFullText) return
    const measure = () => {
      setCollapsedHeight(content.offsetHeight)
      // A button holding the name truncates inside itself, so it is measured too.
      setOverflowing(
        [content, ...content.querySelectorAll('button')].some(
          node => node.scrollWidth > node.clientWidth + 1
        )
      )
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    return () => observer.disconnect()
  }, [children, showFullText])

  return (
    <div
      className={cn(
        'flex w-fit min-w-0 max-w-full items-start gap-1',
        className
      )}
    >
      <motion.div
        className='min-w-0 flex-1 overflow-hidden'
        initial={false}
        animate={
          collapsedHeight > 0
            ? { height: expanded ? 'auto' : collapsedHeight }
            : undefined
        }
        transition={transition}
        onAnimationComplete={() => {
          if (!expanded) setShowFullText(false)
        }}
      >
        <span
          id={contentId}
          ref={contentRef}
          className={cn(
            'block max-w-full',
            // An expanded name breaks anywhere so each line runs up to the
            // toggle, rather than leaving a gap before a long word.
            // Collapsed, a block child (a paragraph around the link) flows
            // inline, so the ellipsis lands on the name. A button holding the
            // name (a drawer trigger) is its own box: it truncates, or wraps
            // when expanded, within the cell.
            '[&_button]:max-w-full [&_button]:align-bottom',
            showFullText
              ? 'whitespace-normal break-all'
              : 'truncate [&>div]:contents [&>p]:contents [&_button]:truncate'
          )}
        >
          {children}
        </span>
      </motion.div>
      {overflowing && (
        <button
          type='button'
          aria-label={expanded ? 'Collapse name' : 'Expand name'}
          aria-expanded={expanded}
          aria-controls={contentId}
          className='text-surface-title-50 focus-visible:ring-primary-200 shrink-0 cursor-pointer rounded outline-none focus-visible:ring-2'
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            if (!expanded) setShowFullText(true)
            setExpanded(!expanded)
            onExpandedChange?.(!expanded)
          }}
        >
          <motion.span
            className='inline-flex'
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={transition}
          >
            <Icon
              name='expand_more'
              className='size-(--text-body-lg--line-height) text-(length:--text-body-lg--line-height)/none'
              aria-hidden='true'
            />
          </motion.span>
        </button>
      )}
    </div>
  )
}
